import * as React from 'react'

import { Box, Checkbox, Popover, Table } from '@factorialco/deprecated-design-system'

import { observer } from 'mobx-react'

import { useUnsafeEffect } from 'modules/core/hooks/useEffect'
import type FiltersForm from 'modules/core/lib/forms/Filters'
import i18n from 'modules/core/lib/i18n'

import { CreatePermissionContext } from '../CreatePermissionContext'

interface SortableHeaderProps {
  attribute: string
  filtersForm: FiltersForm
  left?: React.ReactNode
}

const SortableHeader = observer(
  ({ left, attribute, filtersForm, children }: React.PropsWithChildren<SortableHeaderProps>) => {
    const sortDirection = filtersForm.get('sortDirection')
    const sortAttribute = filtersForm.get('sortAttribute')

    return (
      <Table.SortableHeadCell
        left={left}
        onSort={(attr: string, direction: string) => {
          sortDirection.set(direction)
          sortAttribute.set(attr)
        }}
        attribute={attribute}
        sortAttribute={sortAttribute.value}
        sortDirection={sortDirection.value}
      >
        {children}
      </Table.SortableHeadCell>
    )
  }
)
interface Props {
  filtersForm: FiltersForm
  canBulkEdit?: boolean
  onSelectAll?: (value: boolean) => void
  showSelectEmployeesTooltip?: boolean
  onClose: () => void
}

const Header = ({
  filtersForm,
  canBulkEdit,
  onSelectAll,
  showSelectEmployeesTooltip,
  onClose,
}: Props) => {
  const canCreateEmployee = React.useContext(CreatePermissionContext).authorization
  const [allEmployeeSelected, setAllEmployeeSelected] = React.useState<boolean>(false)
  const noToggle = () => () => undefined

  const onCheckboxChange = () => {
    setAllEmployeeSelected(!allEmployeeSelected)

    if (onSelectAll) onSelectAll(!allEmployeeSelected)
  }

  useUnsafeEffect(() => {
    if (showSelectEmployeesTooltip) {
      const timeout = setTimeout(() => {
        onClose()
      }, 2500)

      return () => clearTimeout(timeout)
    }
  }, [showSelectEmployeesTooltip, onClose])

  const TableCheckBox = () => {
    if (!canBulkEdit) return null

    return (
      <>
        <Popover.ItemList
          attachment="bottom center"
          toggle={noToggle}
          open={!!showSelectEmployeesTooltip}
          title={i18n.t('employees.bulk_edit.no_employees_modal.description')}
        >
          <Box flexDirection="row" alignItems="center">
            <Checkbox
              ariaLabel={i18n.t('common.select_all')}
              checked={allEmployeeSelected}
              onChange={onCheckboxChange}
            />
          </Box>
        </Popover.ItemList>
      </>
    )
  }

  return (
    <Table.Head>
      <Table.Row>
        <Table.HeadCell width={110} left={<TableCheckBox />} paddingX="s16" />
        <SortableHeader filtersForm={filtersForm} attribute="first_name">
          {i18n.t('employee_form.first_name')}
        </SortableHeader>
        <SortableHeader filtersForm={filtersForm} attribute="last_name">
          {i18n.t('employee_form.last_name')}
        </SortableHeader>
        <SortableHeader filtersForm={filtersForm} attribute="job">
          {i18n.t('common.job')}
        </SortableHeader>
        <SortableHeader filtersForm={filtersForm} attribute="starts_on">
          {i18n.t('employee_form.hire_date')}
        </SortableHeader>
        {canCreateEmployee && (
          <SortableHeader filtersForm={filtersForm} attribute="status">
            {i18n.t('employee_form.status')}
          </SortableHeader>
        )}
        <Table.HeadCell width={120} />
      </Table.Row>
    </Table.Head>
  )
}

export default observer(Header)
